import axios from 'axios'
import { getToken } from '@/utils/auth'
import { handleLoginExpired, resolveAssetUrl } from '@/utils/request'

export function resolveAttachmentUrl(url) {
    if (!url) return ''
    if (/^(https?:|blob:|data:)/i.test(url)) return url
    return resolveAssetUrl(url)
}

export function getAttachmentUrl(file) {
    if (!file) return ''
    return resolveAttachmentUrl(file.file_url || file.download_url || file.url || '')
}

function attachmentExtension(file) {
    if (!file) return ''
    const extension = String(file.extension || '').replace(/^\./, '').trim().toLowerCase()
    if (extension) return extension
    const name = file.original_name || file.name || file.file_name || file.file_url || file.download_url || file.url || ''
    return ((String(name).split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i) || [])[1] || '').toLowerCase()
}

export function isImageAttachment(file) {
    if (!file) return false
    if (Number(file.is_image) === 1 || file.isImage === true) return true
    const mimeType = file.mime_type || file.mimeType || file.content_type || file.contentType || ''
    if (/^image\//i.test(mimeType)) return true
    return /^(jpe?g|png|gif|bmp|webp|heic|avif|svg|ico)$/.test(attachmentExtension(file))
}

export function isPdfAttachment(file) {
    if (!file) return false
    const mimeType = file.mime_type || file.mimeType || file.content_type || file.contentType || ''
    if (/^application\/pdf(?:;|$)/i.test(mimeType)) return true
    return attachmentExtension(file) === 'pdf'
}

export function canPreviewAttachment(file) {
    return isImageAttachment(file) || isPdfAttachment(file)
}

export function getOfficeAttachmentType(file) {
    if (!file) return ''
    const extension = attachmentExtension(file)
    if (extension) return /^(docx?|xlsx?)$/i.test(extension) ? extension.toLowerCase() : ''
    const mime = file.mime_type || file.mimeType || file.content_type || file.contentType || ''
    const types = {
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/vnd.ms-excel': 'xls',
    }
    return types[String(mime).split(';')[0].trim().toLowerCase()] || ''
}

export function fetchAttachmentBlob(url) {
    const attachmentUrl = resolveAttachmentUrl(url)
    const token = getToken()

    return axios
        .create()
        .get(attachmentUrl, {
            responseType: 'blob',
            headers: token ? { Authorization: 'Bearer ' + token } : {},
        })
        .then((res) => {
            const contentType = res.headers && res.headers['content-type']
            if (contentType && contentType.indexOf('application/json') !== -1) {
                return parseAttachmentError(res.data)
            }
            return res.data
        }, (error) => {
            const response = error && error.response
            const data = response && response.data
            if (data instanceof Blob) return parseAttachmentError(data)
            if ((data && data.code === 402) || (response && response.status === 402)) handleLoginExpired(data)
            return Promise.reject(error)
        })
}

export function fetchAttachmentBlobUrl(url) {
    return fetchAttachmentBlob(url).then((blob) => window.URL.createObjectURL(blob))
}

export function downloadAttachment(url, name) {
    return fetchAttachmentBlob(url).then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = name || '附件'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.setTimeout(() => {
            window.URL.revokeObjectURL(downloadUrl)
        }, 1000)
    })
}

function parseAttachmentError(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            try {
                const data = JSON.parse(reader.result || '{}')
                if (data && data.code === 402) handleLoginExpired(data)
                reject(data)
            } catch (error) {
                reject({ msg: '附件请求失败' })
            }
        }
        reader.onerror = () => reject({ msg: '附件请求失败' })
        reader.readAsText(blob)
    })
}
